import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Paper,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    Menu,
    MenuItem,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Tooltip,
    Checkbox,
    Tabs,
    Tab,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Switch,
    FormControlLabel,
} from '@mui/material'
import {
    Search,
    FilterList,
    Refresh,
    Visibility,
    Archive as ArchiveIcon,
    Unarchive,
    Email,
    Settings,
    DragIndicator,
    Close,
    Person,
    Send,
    Comment,
    Download,
    Chat,
    Forum,
    EditNote,
    Inventory2,
} from '@mui/icons-material'
import { useTranslation } from '@i18n'
import { useSubmissions } from '@hooks/useSubmissions'
import { useSubmissionStore } from '@store/submissionStore'
import { useAuthStore } from '@store/authStore'
import { formatDate, formatRelativeTime } from '@utils/helpers'
import { submissionsApi } from '@api/submissions'
import { formsApi } from '@api/forms'
import { exportSubmissionsToExcel } from '@utils/exportUtils'
import StatusChip from '@components/Common/StatusChip'
import Loading from '@components/Common/Loading'
import StatsCards from '@components/Dashboard/StatsCards'
import { useFormStats } from '@hooks/useFormStats'
import { CircularProgress, Paper as MuiPaper, Select, FormControl, InputLabel, ClickAwayListener, Popper, Fade } from '@mui/material'
import { Label } from '@mui/icons-material'
import axios from '@api/axios'

// Default column configuration
const DEFAULT_COLUMNS = [
    { id: 'submission_id', label: 'submissions.submissionId', labelEn: 'Submission ID', labelAr: 'رقم الطلب', visible: true, required: true },
    { id: 'end_user_name', label: 'Submitted By', labelEn: 'Submitted By', labelAr: 'مقدم الطلب', visible: true, required: false },
    { id: 'preview', label: 'Preview', labelEn: 'Preview', labelAr: 'معاينة', visible: true, required: false },
    { id: 'status', label: 'submissions.status', labelEn: 'Status', labelAr: 'الحالة', visible: true, required: false },
    { id: 'responded_by', label: 'Responded By', labelEn: 'Responded By', labelAr: 'الرد من', visible: true, required: false },
    { id: 'categories', label: 'Categories', labelEn: 'Categories', labelAr: 'التصنيفات', visible: true, required: false },
    { id: 'date', label: 'submissions.date', labelEn: 'Date', labelAr: 'التاريخ', visible: true, required: false },
]

const STORAGE_KEY = 'submissionColumnSettings'

const Submissions = () => {
    const { t, language } = useTranslation()
    const navigate = useNavigate()
    const { user } = useAuthStore()
    const isDepartmentUser = user?.user_type === 'department'
    const isFormManager = user?.user_type === 'form_manager'
    const {
        submissions,
        pagination,
        isLoading,
        refetch,
        archive,
        restore,
        isArchiving,
        isRestoring,
    } = useSubmissions()

    // Stats for summary cards
    const { stats, isLoading: statsLoading, refetch: refetchStats } = useFormStats()

    const {
        filters,
        setFilter,
        setPage,
        resetFilters,
        selectedSubmissions,
        selectSubmission,
        selectAllSubmissions,
        clearSelection,
    } = useSubmissionStore()

    const [searchValue, setSearchValue] = useState(filters.search || '')
    const [filterAnchor, setFilterAnchor] = useState(null)
    const [actionAnchor, setActionAnchor] = useState(null)
    const [selectedRow, setSelectedRow] = useState(null)
    const [columnSettingsOpen, setColumnSettingsOpen] = useState(false)
    const [columns, setColumns] = useState(DEFAULT_COLUMNS)
    const [draggedItem, setDraggedItem] = useState(null)

    // Live search state
    const [searchResults, setSearchResults] = useState([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [showSearchResults, setShowSearchResults] = useState(false)
    const searchRef = useRef(null)
    const searchTimerRef = useRef(null)

    // Response details dialog state
    const [responseDialogOpen, setResponseDialogOpen] = useState(false)
    const [responseData, setResponseData] = useState(null)

    // Form fields state for dynamic columns and filters
    const [allForms, setAllForms] = useState([])
    const [formFields, setFormFields] = useState([])
    const [filterableFields, setFilterableFields] = useState([])
    const [activeFieldFilters, setActiveFieldFilters] = useState([])
    const [fieldFilterAnchor, setFieldFilterAnchor] = useState(null)
    const [selectedFilterField, setSelectedFilterField] = useState(null)
    const [selectedFilterValue, setSelectedFilterValue] = useState(null)

    // Export language dialog state
    const [exportDialogOpen, setExportDialogOpen] = useState(false)
    const [exportLanguage, setExportLanguage] = useState(language)
    const [selectedExportFields, setSelectedExportFields] = useState(null) // null = all fields
    const [selectedSystemColumns, setSelectedSystemColumns] = useState(null) // null = all system columns

    // System columns definition for export dialog
    const SYSTEM_COLUMNS = [
        { id: 'submission_id', labelEn: 'Submission ID', labelAr: 'رقم التقديم' },
        { id: 'form_title', labelEn: 'Form', labelAr: 'النموذج' },
        { id: 'status', labelEn: 'Status', labelAr: 'الحالة' },
        { id: 'created_at', labelEn: 'Submitted At', labelAr: 'تاريخ التقديم' },
        { id: 'responded_by', labelEn: 'Responded By', labelAr: 'الرد من' },
        { id: 'full_name', labelEn: 'Full Name', labelAr: 'الاسم الكامل' },
    ]
    const [selectedSubmissionForResponse, setSelectedSubmissionForResponse] = useState(null)
    const [loadingResponse, setLoadingResponse] = useState(false)
    const [submissionTypeFilter, setSubmissionTypeFilter] = useState('all') // 'all' | 'form' | 'chat'

    // Category filter state
    const [categoryMap, setCategoryMap] = useState({}) // { submissionId: [categories] }
    const [allCategories, setAllCategories] = useState([]) // All unique categories for filtering
    const [categoryFilter, setCategoryFilter] = useState('all') // 'all' or category id

    // Fetch all forms and extract fields on mount
    useEffect(() => {
        const fetchFormsAndFields = async () => {
            console.log('🎯 Starting fetchFormsAndFields...')
            try {
                console.log('📡 Calling formsApi.getAll()...')
                const response = await formsApi.getAll()
                console.log('✅ API Response received:', response)

                const forms = response.data || []
                console.log('📋 Forms data:', forms)
                console.log('📊 Number of forms:', forms.length)
                setAllForms(forms)

                // Extract all unique fields from all forms
                const fieldsMap = new Map()
                const filterableFieldsMap = new Map()

                forms.forEach((form, formIndex) => {
                    console.log(`\n🔍 Processing form #${formIndex + 1}:`, form.title_en || form.title)

                    let fields = form.fields
                    console.log('  Raw fields type:', typeof fields)
                    console.log('  Raw fields value:', fields)

                    if (typeof fields === 'string') {
                        try {
                            fields = JSON.parse(fields)
                            console.log('  ✅ Parsed fields from string')
                        } catch (e) {
                            console.error('  ❌ Failed to parse fields:', e)
                            fields = []
                        }
                    }

                    if (Array.isArray(fields)) {
                        console.log(`  Found ${fields.length} fields in this form`)

                        fields.forEach((field, fieldIndex) => {
                            console.log(`    Field #${fieldIndex + 1}: ${field.id} (${field.type})`)

                            // Parse options if it's a string
                            let fieldOptions = field.options || []
                            console.log('      Raw options:', fieldOptions)

                            if (typeof fieldOptions === 'string') {
                                try {
                                    fieldOptions = JSON.parse(fieldOptions)
                                    console.log('      ✅ Parsed options from string:', fieldOptions)
                                } catch (e) {
                                    console.warn(`      ❌ Failed to parse options for field ${field.id}:`, e)
                                    fieldOptions = []
                                }
                            }

                            if (!fieldsMap.has(field.id)) {
                                fieldsMap.set(field.id, {
                                    id: field.id,
                                    label_en: field.label_en || field.label,
                                    label_ar: field.label_ar || field.label,
                                    type: field.type,
                                    options: fieldOptions,
                                    formId: form.id,
                                    formName: form.title_en || form.title
                                })
                                console.log('      ➕ Added to fieldsMap')
                            }

                            // Track filterable fields (select, radio, checkbox)
                            if (['select', 'radio', 'checkbox'].includes(field.type)) {
                                console.log(`      🎯 FILTERABLE FIELD FOUND!`)
                                console.log(`      Options count: ${fieldOptions?.length || 0}`)

                                if (!filterableFieldsMap.has(field.id)) {
                                    filterableFieldsMap.set(field.id, {
                                        id: field.id,
                                        label_en: field.label_en || field.label,
                                        label_ar: field.label_ar || field.label,
                                        type: field.type,
                                        options: fieldOptions,
                                        formId: form.id,
                                        formName: form.title_en || form.title
                                    })
                                    console.log(`      ➕ Added to filterableFieldsMap with ${fieldOptions?.length || 0} options`)
                                }
                            }
                        })
                    } else {
                        console.warn(`  ⚠️ Fields is not an array:`, typeof fields)
                    }
                })

                const allFormFields = Array.from(fieldsMap.values())
                const allFilterableFields = Array.from(filterableFieldsMap.values())

                console.log('\n📊 FINAL RESULTS:')
                console.log('Total unique form fields:', allFormFields.length)
                console.log('Total filterable fields:', allFilterableFields.length)
                console.log('Filterable fields details:', allFilterableFields)

                setFormFields(allFormFields)
                setFilterableFields(allFilterableFields)

            } catch (error) {
                console.error('💥 ERROR in fetchFormsAndFields:', error)
                console.error('Error stack:', error.stack)
            }
        }

        fetchFormsAndFields()
    }, [])

    // Load column settings from localStorage and merge with dynamic form fields
    useEffect(() => {
        if (formFields.length === 0) return // Wait for fields to be loaded

        const saved = localStorage.getItem(STORAGE_KEY)

        // Build dynamic columns from form fields
        const dynamicColumns = formFields.map(field => ({
            id: `field_${field.id}`,
            label: language === 'ar' ? field.label_ar : field.label_en,
            labelEn: field.label_en,
            labelAr: field.label_ar,
            type: 'form_field',
            fieldId: field.id,
            fieldType: field.type,
            fieldOptions: field.options,
            visible: false, // Hidden by default
            required: false
        }))

        // Merge default columns with dynamic columns
        const allColumns = [...DEFAULT_COLUMNS, ...dynamicColumns]

        if (saved) {
            try {
                const parsed = JSON.parse(saved)
                // Merge with all columns to handle new columns
                const merged = allColumns.map(defaultCol => {
                    const savedCol = parsed.find(c => c.id === defaultCol.id)
                    return savedCol ? { ...defaultCol, visible: savedCol.visible } : defaultCol
                })
                // Reorder based on saved order
                const ordered = []
                parsed.forEach(savedCol => {
                    const col = merged.find(c => c.id === savedCol.id)
                    if (col) ordered.push(col)
                })
                // Add any new columns not in saved
                merged.forEach(col => {
                    if (!ordered.find(c => c.id === col.id)) ordered.push(col)
                })
                setColumns(ordered)
            } catch (e) {
                console.error('Failed to load column settings', e)
                setColumns(allColumns)
            }
        } else {
            setColumns(allColumns)
        }
    }, [formFields, language])

    // Save column settings to localStorage
    const saveColumnSettings = useCallback((newColumns) => {
        setColumns(newColumns)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newColumns.map(c => ({ id: c.id, visible: c.visible }))))
    }, [])

    const handleColumnToggle = (columnId) => {
        const newColumns = columns.map(col =>
            col.id === columnId && !col.required ? { ...col, visible: !col.visible } : col
        )
        saveColumnSettings(newColumns)
    }

    const handleDragStart = (e, index) => {
        setDraggedItem(index)
        e.dataTransfer.effectAllowed = 'move'
    }

    const handleDragOver = (e, index) => {
        e.preventDefault()
        if (draggedItem === null || draggedItem === index) return

        const newColumns = [...columns]
        const draggedCol = newColumns[draggedItem]
        newColumns.splice(draggedItem, 1)
        newColumns.splice(index, 0, draggedCol)
        setColumns(newColumns)
        setDraggedItem(index)
    }

    const handleDragEnd = () => {
        setDraggedItem(null)
        saveColumnSettings(columns)
    }

    const handleSearch = (e) => {
        e.preventDefault()
        setFilter('search', searchValue)
        setShowSearchResults(false)
    }

    // Debounced live search
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        if (!searchValue || searchValue.length < 2) {
            setSearchResults([])
            setShowSearchResults(false)
            return
        }
        setSearchLoading(true)
        searchTimerRef.current = setTimeout(async () => {
            try {
                const response = await submissionsApi.search(searchValue)
                const results = response?.data || response || []
                setSearchResults(Array.isArray(results) ? results : [])
                setShowSearchResults(true)
            } catch (err) {
                console.error('Live search error:', err)
                setSearchResults([])
            } finally {
                setSearchLoading(false)
            }
        }, 300)
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
    }, [searchValue])

    const handleTabChange = (_, newValue) => {
        if (newValue === 0) {
            setFilter('archived', false)
        } else {
            setFilter('archived', true)
        }
        setFilter('status', null)
    }

    const handleStatusFilter = (status) => {
        setFilter('status', status)
        setFilterAnchor(null)
    }

    const handlePageChange = (_, newPage) => {
        setPage(newPage + 1)
    }

    const handleRowsPerPageChange = (e) => {
        setFilter('perPage', parseInt(e.target.value, 10))
    }

    const handleActionClick = (event, submission) => {
        setActionAnchor(event.currentTarget)
        setSelectedRow(submission)
    }

    const handleActionClose = () => {
        setActionAnchor(null)
        setSelectedRow(null)
    }

    const handleArchive = () => {
        if (selectedRow) {
            archive(selectedRow.id)
        }
        handleActionClose()
    }

    const handleRestore = () => {
        if (selectedRow) {
            restore(selectedRow.id)
        }
        handleActionClose()
    }

    // Get responded by info from submission
    const getRespondedBy = (submission) => {
        // Check if there's email response data
        if (submission.responded_by_name_en || submission.responded_by_name_ar) {
            return language === 'ar' ? submission.responded_by_name_ar : submission.responded_by_name_en
        }
        // Check if status is replied
        if (submission.status === 'replied' || submission.status === 'department_replied') {
            return submission.read_by_name_en || submission.read_by_name_ar || '-'
        }
        return '-'
    }

    // Handle viewing response details
    const handleViewResponse = async (submission) => {
        setLoadingResponse(true)
        setSelectedSubmissionForResponse(submission)
        setResponseDialogOpen(true)

        try {
            const response = await submissionsApi.getById(submission.id)
            // The axios interceptor returns response.data already, so 'response' is {success, message, data}
            const submissionData = response.data
            console.log('Full API response:', response)
            console.log('Submission data:', submissionData)
            console.log('Email responses:', submissionData?.email_responses)
            // Get the most recent email response
            if (submissionData?.email_responses?.length > 0) {
                const latestResponse = submissionData.email_responses[submissionData.email_responses.length - 1]
                console.log('Latest response:', latestResponse)
                setResponseData(latestResponse)
            }
        } catch (error) {
            console.error('Failed to fetch response details:', error)
        } finally {
            setLoadingResponse(false)
        }
    }

    const handleCloseResponseDialog = () => {
        setResponseDialogOpen(false)
        setResponseData(null)
        setSelectedSubmissionForResponse(null)
    }

    const handleExportToExcel = () => {
        if (!submissions || submissions.length === 0) {
            alert(language === 'ar' ? 'لا توجد بيانات للتصدير' : 'No data to export')
            return
        }

        // Show language selection dialog
        setExportLanguage(language)
        setSelectedSystemColumns(null) // Reset to all selected
        setSelectedExportFields(null) // Reset to all selected
        setExportDialogOpen(true)
    }

    const handleConfirmExport = async () => {
        try {
            console.log('=== EXPORT DEBUG ===');
            console.log('Submissions:', submissions);
            console.log('First submission:', submissions[0]);

            // Try different possible field names for form ID
            const getFormId = (submission) => {
                return submission.form_id || submission.formId || submission.form?.id || null;
            };

            // Get unique form IDs from submissions
            const formIds = [...new Set(submissions.map(s => getFormId(s)).filter(Boolean))];
            console.log('Form IDs found:', formIds);

            // Fetch form definitions to get field labels
            let allFormFields = [];

            if (formIds.length > 0) {
                console.log('Fetching form definitions for IDs:', formIds);

                // Fetch all form definitions
                const formPromises = formIds.map(id => formsApi.getById(id));
                const formResponses = await Promise.all(formPromises);

                console.log('Form responses:', formResponses);

                // Create a map of form_id to fields
                const formFieldsMap = {};
                formResponses.forEach(response => {
                    if (response.data && response.data.fields) {
                        const fields = typeof response.data.fields === 'string'
                            ? JSON.parse(response.data.fields)
                            : response.data.fields;
                        formFieldsMap[response.data.id] = fields;
                        console.log(`Form ${response.data.id} fields:`, fields);
                    }
                });

                // For single form, use its fields directly
                if (formIds.length === 1 && formFieldsMap[formIds[0]]) {
                    allFormFields = formFieldsMap[formIds[0]];
                    console.log('Using single form fields:', allFormFields);
                } else {
                    // For multiple forms, add form_fields to each submission
                    submissions.forEach(submission => {
                        const formId = getFormId(submission);
                        if (formId && formFieldsMap[formId]) {
                            submission.form_fields = formFieldsMap[formId];
                        }
                    });
                }
            } else {
                console.warn('No form IDs found in submissions. Fetching all forms to match by title...');

                // Fetch all forms and match by title
                const formsResponse = await formsApi.getAll();
                console.log('Forms response:', formsResponse);

                const allForms = formsResponse.data || [];
                console.log('All forms:', allForms);

                // Create a map of form_title to form fields
                const formFieldsByTitle = {};
                allForms.forEach(form => {
                    if (form.fields) {
                        const fields = typeof form.fields === 'string'
                            ? JSON.parse(form.fields)
                            : form.fields;

                        // Map both English and Arabic titles to the same fields
                        if (form.title) {
                            formFieldsByTitle[form.title] = fields;
                        }
                        if (form.title_ar) {
                            formFieldsByTitle[form.title_ar] = fields;
                        }

                        console.log(`Mapped form "${form.title}" / "${form.title_ar}" to fields`);
                    }
                });

                console.log('Form fields map:', formFieldsByTitle);

                // Get form fields from first submission's form_title
                if (submissions[0] && submissions[0].form_title) {
                    allFormFields = formFieldsByTitle[submissions[0].form_title] ||
                        formFieldsByTitle[submissions[0].form_title_ar] ||
                        [];
                    console.log(`Using fields for form: ${submissions[0].form_title}`, allFormFields);
                }

                // If we have multiple forms, add form_fields to each submission
                const uniqueTitles = [...new Set(submissions.map(s => s.form_title || s.form_title_ar).filter(Boolean))];
                if (uniqueTitles.length > 1) {
                    console.log('Multiple forms detected, adding fields to each submission');
                    submissions.forEach(submission => {
                        const title = submission.form_title || submission.form_title_ar;
                        if (title && formFieldsByTitle[title]) {
                            submission.form_fields = formFieldsByTitle[title];
                        }
                    });
                }
            }

            console.log('Final form fields for export:', allFormFields);
            console.log('Export language:', exportLanguage);

            // If specific submissions are selected (checkboxes), export only those
            const submissionsToExport = selectedSubmissions.length > 0
                ? submissions.filter(s => selectedSubmissions.includes(s.id))
                : submissions;

            exportSubmissionsToExcel(submissionsToExport, allFormFields, exportLanguage, selectedExportFields, null, selectedSystemColumns);
            setExportDialogOpen(false);
        } catch (error) {
            console.error('Error fetching form fields for export:', error);
            alert(language === 'ar' ? 'خطأ في تحميل بيانات النموذج' : 'Error loading form data');
        }
    }

    // Fetch categories for visible submissions in bulk
    useEffect(() => {
        const fetchCategories = async () => {
            if (!submissions || submissions.length === 0) return
            try {
                const submissionIds = submissions.map(s => s.id).join(',')
                const response = await axios.get(`/submissions/categories/bulk`, {
                    params: { submission_ids: submissionIds }
                })
                if (response?.success) {
                    setCategoryMap(response.data || {})
                    // Build unique categories list for filtering
                    const catSet = new Map()
                    Object.values(response.data || {}).forEach(cats => {
                        cats.forEach(cat => catSet.set(cat.id, cat))
                    })
                    setAllCategories(Array.from(catSet.values()))
                }
            } catch (err) {
                console.error('Failed to fetch categories:', err)
            }
        }
        fetchCategories()
    }, [submissions])

    const visibleColumns = columns.filter(col => col.visible)

    // Apply field filters to submissions
    const filteredSubmissions = activeFieldFilters.length > 0
        ? submissions.filter(submission => {
            // All filters must match (AND logic)
            return activeFieldFilters.every(filter => {
                const value = submission.data?.[filter.fieldId]
                if (!value) return false

                // Handle array values (checkbox)
                if (Array.isArray(value)) {
                    return value.includes(filter.value)
                }

                // Handle single values
                return value === filter.value
            })
        })
        : submissions

    // Apply submission type filter (chat vs form)
    const typeFilteredSubmissions = submissionTypeFilter === 'all'
        ? filteredSubmissions
        : submissionTypeFilter === 'chat'
            ? filteredSubmissions.filter(s => s.data?.chat_transcript)
            : filteredSubmissions.filter(s => !s.data?.chat_transcript)

    // Apply category filter
    const categoryFilteredSubmissions = categoryFilter === 'all'
        ? typeFilteredSubmissions
        : typeFilteredSubmissions.filter(s => {
            const cats = categoryMap[s.id] || []
            return cats.some(c => String(c.id) === String(categoryFilter))
        })

    const statuses = [
        { value: null, label: 'All' },
        { value: 'new', label: t('status.new') },
        { value: 'read', label: t('status.read') },
        { value: 'replied', label: t('status.replied') },
        { value: 'pending_department', label: t('status.pending_department') },
        { value: 'department_replied', label: t('status.department_replied') },
    ]

    const renderCellContent = (submission, columnId) => {
        // Check if this is a dynamic form field column
        const column = columns.find(c => c.id === columnId)
        if (column && column.type === 'form_field') {
            const fieldId = column.fieldId
            const value = submission.data?.[fieldId]

            if (!value || value === '') {
                return <Typography variant="body2" color="text.secondary">-</Typography>
            }

            // Handle different field types
            switch (column.fieldType) {
                case 'select':
                case 'radio':
                    // Find the label for the selected value
                    const option = column.fieldOptions?.find(opt => opt.value === value)
                    const displayValue = option ? (language === 'ar' ? option.label_ar || option.label : option.label) : value
                    return (
                        <Typography variant="body2">
                            {displayValue}
                        </Typography>
                    )
                case 'checkbox':
                    // Checkbox can be array or single value
                    if (Array.isArray(value)) {
                        return (
                            <Typography variant="body2">
                                {value.map(v => {
                                    const opt = column.fieldOptions?.find(o => o.value === v)
                                    return opt ? (language === 'ar' ? opt.label_ar || opt.label : opt.label) : v
                                }).join(', ')}
                            </Typography>
                        )
                    }
                    return <Typography variant="body2">{value}</Typography>
                case 'file':
                    return (
                        <Typography variant="body2" color="primary.main" sx={{ textDecoration: 'underline' }}>
                            {value.split('/').pop()}
                        </Typography>
                    )
                case 'date':
                    return (
                        <Typography variant="body2">
                            {formatDate(value, 'PP', language)}
                        </Typography>
                    )
                default:
                    return (
                        <Typography
                            variant="body2"
                            sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {value}
                        </Typography>
                    )
            }
        }

        // Handle system columns
        switch (columnId) {
            case 'submission_id':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                            {submission.submission_id}
                        </Typography>
                        {submission.data?.chat_transcript && (
                            <Chip
                                icon={<Chat sx={{ fontSize: 14 }} />}
                                label={language === 'ar' ? 'محادثة' : 'Chat'}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-icon': { fontSize: 14 } }}
                            />
                        )}
                    </Box>
                )
            case 'end_user_name':
                // Show end-user name if available, otherwise show "Public Submission"
                const endUserName = language === 'ar'
                    ? (submission.end_user_name_ar || submission.end_user_name_en)
                    : (submission.end_user_name_en || submission.end_user_name_ar)

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Person fontSize="small" color={endUserName ? "primary" : "action"} />
                        <Typography variant="body2" color={endUserName ? "text.primary" : "text.secondary"}>
                            {endUserName || (language === 'ar' ? 'طلب عام' : 'Public Submission')}
                        </Typography>
                    </Box>
                )
            case 'preview':
                return (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            maxWidth: 250,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {Object.values(submission.data || {}).slice(0, 2).join(', ')}
                    </Typography>
                )
            case 'categories':
                const cats = categoryMap[submission.id] || []
                if (cats.length === 0) return <Typography variant="body2" color="text.secondary">-</Typography>
                return (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {cats.map(cat => (
                            <Chip
                                key={cat.id}
                                icon={<Label sx={{ fontSize: 14 }} />}
                                label={language === 'ar' ? (cat.name_ar || cat.name_en) : cat.name_en}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    backgroundColor: cat.color + '22',
                                    color: cat.color,
                                    borderColor: cat.color,
                                    '& .MuiChip-icon': { color: cat.color, fontSize: 14 }
                                }}
                                variant="outlined"
                            />
                        ))}
                    </Box>
                )
            case 'status':
                return <StatusChip status={submission.status} />
            case 'responded_by':
                const respondedBy = getRespondedBy(submission)
                return respondedBy !== '-' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Person fontSize="small" color="action" />
                        <Typography variant="body2">{respondedBy}</Typography>
                        <Tooltip title={language === 'ar' ? 'عرض تفاصيل الرد' : 'View Response Details'}>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleViewResponse(submission)
                                }}
                                sx={{ ml: 0.5, p: 0.25 }}
                            >
                                <Visibility fontSize="small" color="primary" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">-</Typography>
                )
            case 'date':
                return (
                    <Tooltip title={formatDate(submission.created_at, 'PPP p', language)}>
                        <Typography variant="body2" color="text.secondary">
                            {formatRelativeTime(submission.created_at, language)}
                        </Typography>
                    </Tooltip>
                )
            default:
                return null
        }
    }

    return (
        <Box>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Typography variant="h4" fontWeight={700}>
                    {t('submissions.title')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={language === 'ar' ? 'إعدادات الأعمدة' : 'Column Settings'}>
                        <IconButton onClick={() => setColumnSettingsOpen(true)}>
                            <Settings />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={handleExportToExcel}
                        disabled={isLoading || !submissions || submissions.length === 0}
                    >
                        {language === 'ar' ? 'تصدير إلى Excel' : 'Export to Excel'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => {
                            const chatSubs = (submissions || []).filter(s => s.data?.chat_transcript)
                            if (!chatSubs.length) {
                                alert(language === 'ar' ? 'لا توجد محادثات للتصدير' : 'No chat submissions to export')
                                return
                            }
                            const headers = ['ID', 'Visitor', 'Email', 'Status', 'Duration', 'Agents', 'Messages', 'Rating', 'Created', 'Closed']
                            const rows = chatSubs.map(s => {
                                const d = s.data
                                return [
                                    s.submission_id,
                                    d.chat_visitor_name || '',
                                    d.chat_visitor_email || '',
                                    d.chat_status || '',
                                    d.chat_duration_formatted || '',
                                    d.chat_agents || '',
                                    d.chat_total_messages || 0,
                                    d.chat_rating || 0,
                                    d.chat_created_at || '',
                                    d.chat_closed_at || '',
                                ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
                            })
                            const csv = '\uFEFF' + [headers.join(','), ...rows].join('\n')
                            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                            const url = URL.createObjectURL(blob)
                            const a = document.createElement('a')
                            a.href = url
                            a.download = `chat_submissions_${new Date().toISOString().split('T')[0]}.csv`
                            a.click()
                            URL.revokeObjectURL(url)
                        }}
                        disabled={isLoading || !submissions || !submissions.some(s => s.data?.chat_transcript)}
                        color="secondary"
                    >
                        {language === 'ar' ? 'تصدير المحادثات CSV' : 'Export Chats CSV'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={() => refetch()}
                        disabled={isLoading}
                    >
                        {t('common.refresh')}
                    </Button>
                </Box>
            </Box>

            {/* Stats Cards */}
            {!statsLoading && stats && (
                <Box sx={{ mb: 3 }}>
                    <StatsCards stats={stats} />
                </Box>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 3, borderRadius: 2 }}>
                <Tabs
                    value={filters.archived ? 1 : 0}
                    onChange={handleTabChange}
                    sx={{ px: 2 }}
                >
                    <Tab label={t('submissions.activeSubmissions')} />
                    <Tab label={t('submissions.archivedSubmissions')} />
                </Tabs>
                <Divider />

                {/* Search and Filters */}
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                    }}
                >
                    <Box component="form" onSubmit={handleSearch} sx={{ flex: 1, minWidth: 200, position: 'relative' }} ref={searchRef}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={t('submissions.searchPlaceholder')}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onFocus={() => { if (searchResults.length > 0) setShowSearchResults(true) }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                                endAdornment: searchLoading ? (
                                    <InputAdornment position="end">
                                        <CircularProgress size={18} />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        {/* Live Search Results Dropdown */}
                        {showSearchResults && searchResults.length > 0 && (
                            <ClickAwayListener onClickAway={() => setShowSearchResults(false)}>
                                <Paper
                                    elevation={8}
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        zIndex: 1300,
                                        maxHeight: 400,
                                        overflowY: 'auto',
                                        mt: 0.5,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            {language === 'ar'
                                                ? `${searchResults.length} نتيجة — كل الأنواع`
                                                : `${searchResults.length} results — all types`}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {['chat','direct','normal'].map(type => {
                                                const count = searchResults.filter(r => r.source === type).length
                                                if (count === 0) return null
                                                const cfg = {
                                                    chat:   { label: language === 'ar' ? 'محادثة' : 'Chat',   color: 'info',    icon: <Forum sx={{ fontSize: 11 }} /> },
                                                    direct: { label: language === 'ar' ? 'مباشر' : 'Direct', color: 'warning', icon: <EditNote sx={{ fontSize: 11 }} /> },
                                                    normal: { label: language === 'ar' ? 'طلب'    : 'Form',   color: 'default',  icon: <Send sx={{ fontSize: 11 }} /> },
                                                }[type]
                                                return (
                                                    <Chip key={type} icon={cfg.icon} label={`${count} ${cfg.label}`} size="small" color={cfg.color} variant="outlined"
                                                        sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-icon': { fontSize: 11 } }} />
                                                )
                                            })}
                                        </Box>
                                    </Box>
                                    {searchResults.map((result) => (
                                        <Box
                                            key={result.id}
                                            onClick={() => {
                                                setShowSearchResults(false)
                                                navigate(`/submissions/${result.id}`)
                                            }}
                                            sx={{
                                                px: 2,
                                                py: 1.5,
                                                cursor: 'pointer',
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                                '&:hover': { bgcolor: 'action.hover' },
                                                '&:last-child': { borderBottom: 'none' },
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                            }}
                                        >
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3, flexWrap: 'wrap' }}>
                                                    {/* Source type icon */}
                                                    {result.source === 'chat' && (
                                                        <Tooltip title={language === 'ar' ? 'محادثة مباشرة' : 'Live Chat'}>
                                                            <Forum sx={{ fontSize: 15, color: 'info.main', flexShrink: 0 }} />
                                                        </Tooltip>
                                                    )}
                                                    {result.source === 'direct' && (
                                                        <Tooltip title={language === 'ar' ? 'إدخال مباشر' : 'Direct Entry'}>
                                                            <EditNote sx={{ fontSize: 15, color: 'warning.main', flexShrink: 0 }} />
                                                        </Tooltip>
                                                    )}
                                                    {(!result.source || result.source === 'normal') && (
                                                        <Tooltip title={language === 'ar' ? 'طلب عادي' : 'Form Submission'}>
                                                            <Send sx={{ fontSize: 13, color: 'success.main', flexShrink: 0 }} />
                                                        </Tooltip>
                                                    )}
                                                    <Typography variant="body2" fontWeight={700} noWrap>
                                                        {result.submission_id}
                                                    </Typography>
                                                    <StatusChip status={result.status} size="small" />
                                                    {result.archived == 1 && (
                                                        <Chip
                                                            label={language === 'ar' ? 'مؤرشف' : 'Archived'}
                                                            size="small"
                                                            icon={<Inventory2 sx={{ fontSize: '14px !important' }} />}
                                                            sx={{
                                                                height: 20,
                                                                fontSize: '0.65rem',
                                                                bgcolor: 'warning.light',
                                                                color: 'warning.contrastText',
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                                {result.preview && (
                                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                                        {result.preview}
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                                                    {language === 'ar' ? result.form_title_ar : result.form_title}
                                                    {' · '}
                                                    {formatRelativeTime(result.created_at, language)}
                                                </Typography>
                                            </Box>
                                            <Visibility sx={{ fontSize: 18, color: 'text.disabled' }} />
                                        </Box>
                                    ))}
                                </Paper>
                            </ClickAwayListener>
                        )}
                        {showSearchResults && searchResults.length === 0 && searchValue.length >= 2 && !searchLoading && (
                            <ClickAwayListener onClickAway={() => setShowSearchResults(false)}>
                                <Paper
                                    elevation={8}
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        zIndex: 1300,
                                        mt: 0.5,
                                        borderRadius: 2,
                                        p: 2,
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        {language === 'ar' ? 'لم يتم العثور على نتائج' : 'No results found'}
                                    </Typography>
                                </Paper>
                            </ClickAwayListener>
                        )}
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<FilterList />}
                        onClick={(e) => setFilterAnchor(e.currentTarget)}
                    >
                        {filters.status ? t(`status.${filters.status}`) : t('submissions.filterByStatus')}
                    </Button>



                    {/* Category Filter */}
                    {allCategories.length > 0 && (
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>{language === 'ar' ? 'التصنيف' : 'Category'}</InputLabel>
                            <Select
                                value={categoryFilter}
                                label={language === 'ar' ? 'التصنيف' : 'Category'}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                            >
                                <MenuItem value="all">{language === 'ar' ? 'الكل' : 'All'}</MenuItem>
                                {allCategories.map(cat => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: cat.color }} />
                                            {language === 'ar' ? (cat.name_ar || cat.name_en) : cat.name_en}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {filterableFields.length > 0 && (
                        <Button
                            variant="outlined"
                            startIcon={<FilterList />}
                            onClick={(e) => setFieldFilterAnchor(e.currentTarget)}
                            color="secondary"
                        >
                            {language === 'ar' ? '+ تصفية متقدمة' : '+ Add Field Filter'}
                        </Button>
                    )}

                    {/* Active Field Filters */}
                    {activeFieldFilters.map((filter, index) => {
                        const field = filterableFields.find(f => f.id === filter.fieldId)
                        const option = field?.options?.find(opt => opt.value === filter.value)
                        const fieldLabel = field ? (language === 'ar' ? field.label_ar : field.label_en) : ''
                        const valueLabel = option ? (language === 'ar' ? option.label_ar || option.label : option.label) : filter.value

                        return (
                            <Chip
                                key={index}
                                label={`${fieldLabel}: ${valueLabel}`}
                                onDelete={() => {
                                    setActiveFieldFilters(prev => prev.filter((_, i) => i !== index))
                                }}
                                color="primary"
                                variant="outlined"
                            />
                        )
                    })}

                    {(filters.status || filters.search || activeFieldFilters.length > 0 || submissionTypeFilter !== 'all' || categoryFilter !== 'all') && (
                        <Button
                            size="small"
                            onClick={() => {
                                resetFilters()
                                setSearchValue('')
                                setActiveFieldFilters([])
                                setSubmissionTypeFilter('all')
                                setCategoryFilter('all')
                            }}
                        >
                            Clear Filters
                        </Button>
                    )}
                </Box>
            </Paper>

            {/* Status Filter Menu */}
            <Menu
                anchorEl={filterAnchor}
                open={Boolean(filterAnchor)}
                onClose={() => setFilterAnchor(null)}
            >
                {statuses.map((status) => (
                    <MenuItem
                        key={status.value || 'all'}
                        selected={filters.status === status.value}
                        onClick={() => handleStatusFilter(status.value)}
                    >
                        {status.label}
                    </MenuItem>
                ))}
            </Menu>

            {/* Field Filter Dialog */}
            <Dialog
                open={Boolean(fieldFilterAnchor)}
                onClose={() => {
                    setFieldFilterAnchor(null)
                    setSelectedFilterField(null)
                    setSelectedFilterValue(null)
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {language === 'ar' ? 'إضافة تصفية حقل' : 'Add Field Filter'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        {/* Field Selection */}
                        <TextField
                            select
                            fullWidth
                            label={language === 'ar' ? 'اختر الحقل' : 'Select Field'}
                            value={selectedFilterField || ''}
                            onChange={(e) => {
                                setSelectedFilterField(e.target.value)
                                setSelectedFilterValue(null) // Reset value when field changes
                            }}
                        >
                            {filterableFields.map(field => (
                                <MenuItem key={field.id} value={field.id}>
                                    {language === 'ar' ? field.label_ar : field.label_en}
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        ({field.formName})
                                    </Typography>
                                </MenuItem>
                            ))}
                        </TextField>

                        {/* Value Selection - only show when field is selected */}
                        {selectedFilterField && (() => {
                            const field = filterableFields.find(f => f.id === selectedFilterField)
                            console.log('Selected field:', field)
                            console.log('Field options:', field?.options)
                            return field ? (
                                <TextField
                                    select
                                    fullWidth
                                    label={language === 'ar' ? 'اختر القيمة' : 'Select Value'}
                                    value={selectedFilterValue || ''}
                                    onChange={(e) => setSelectedFilterValue(e.target.value)}
                                >
                                    {field.options?.map((option, idx) => {
                                        console.log('Rendering option:', option)
                                        return (
                                            <MenuItem key={idx} value={option.value}>
                                                {language === 'ar' ? option.label_ar || option.label : option.label}
                                            </MenuItem>
                                        )
                                    })}
                                </TextField>
                            ) : null
                        })()}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setFieldFilterAnchor(null)
                        setSelectedFilterField(null)
                        setSelectedFilterValue(null)
                    }}>
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (selectedFilterField && selectedFilterValue) {
                                // Add the filter
                                setActiveFieldFilters(prev => [
                                    ...prev,
                                    { fieldId: selectedFilterField, value: selectedFilterValue }
                                ])
                                // Reset and close
                                setFieldFilterAnchor(null)
                                setSelectedFilterField(null)
                                setSelectedFilterValue(null)
                            }
                        }}
                        disabled={!selectedFilterField || !selectedFilterValue}
                    >
                        {language === 'ar' ? 'إضافة' : 'Add Filter'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Table */}
            <Paper sx={{ borderRadius: 2 }}>
                {isLoading ? (
                    <Box sx={{ p: 4 }}>
                        <Loading message={t('common.loading')} />
                    </Box>
                ) : filteredSubmissions.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                            {t('submissions.noSubmissions')}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedSubmissions.length === filteredSubmissions.length}
                                                indeterminate={
                                                    selectedSubmissions.length > 0 &&
                                                    selectedSubmissions.length < filteredSubmissions.length
                                                }
                                                onChange={(e) =>
                                                    e.target.checked
                                                        ? selectAllSubmissions(filteredSubmissions.map((s) => s.id))
                                                        : clearSelection()
                                                }
                                            />
                                        </TableCell>
                                        {visibleColumns.map((column) => (
                                            <TableCell key={column.id}>
                                                {column.label.startsWith('submissions.') ? t(column.label) : (language === 'ar' ? column.labelAr : column.labelEn)}
                                            </TableCell>
                                        ))}
                                        <TableCell align="right">{t('common.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {categoryFilteredSubmissions.map((submission) => (
                                        <TableRow
                                            key={submission.id}
                                            hover
                                            selected={selectedSubmissions.includes(submission.id)}
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => navigate(`/submissions/${submission.id}`)}
                                        >
                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    checked={selectedSubmissions.includes(submission.id)}
                                                    onChange={() => selectSubmission(submission.id)}
                                                />
                                            </TableCell>
                                            {visibleColumns.map((column) => (
                                                <TableCell key={column.id}>
                                                    {renderCellContent(submission, column.id)}
                                                </TableCell>
                                            ))}
                                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                                <Tooltip title={language === 'ar' ? 'عرض' : 'View'}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`/submissions/${submission.id}`)}
                                                    >
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                                {isDepartmentUser ? (
                                                    <Tooltip title={language === 'ar' ? 'إضافة تعليق' : 'Add Comment'}>
                                                        <IconButton
                                                            size="small"
                                                            color="secondary"
                                                            onClick={() => navigate(`/submissions/${submission.id}?tab=comments`)}
                                                        >
                                                            <Comment />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : isFormManager ? (
                                                    // Form Managers only get View and Archive - no Email or Navigate
                                                    null
                                                ) : (
                                                    <>
                                                        <Tooltip title={language === 'ar' ? 'إرسال بريد' : 'Send Email'}>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => navigate(`/submissions/${submission.id}?tab=email`)}
                                                            >
                                                                <Email />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={language === 'ar' ? 'تحويل للقسم' : 'Navigate to Department'}>
                                                            <IconButton
                                                                size="small"
                                                                sx={{ color: '#1a3a6b' }}
                                                                onClick={() => navigate(`/submissions/${submission.id}?tab=navigate`)}
                                                            >
                                                                <Send />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                                {!filters.archived ? (
                                                    <Tooltip title={language === 'ar' ? 'أرشفة' : 'Archive'}>
                                                        <IconButton
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => archive(submission.id)}
                                                            disabled={isArchiving}
                                                        >
                                                            <ArchiveIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title={language === 'ar' ? 'استعادة' : 'Restore'}>
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            onClick={() => restore(submission.id)}
                                                            disabled={isRestoring}
                                                        >
                                                            <Unarchive />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={pagination.total}
                            page={pagination.page - 1}
                            rowsPerPage={pagination.per_page}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={[10, 20, 50, 100, { value: -1, label: language === 'ar' ? 'الكل' : 'All' }]}
                        />
                    </>
                )}
            </Paper>

            {/* Column Settings Dialog */}
            <Dialog
                open={columnSettingsOpen}
                onClose={() => setColumnSettingsOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Settings color="primary" />
                        {language === 'ar' ? 'إعدادات الأعمدة' : 'Column Settings'}
                    </Box>
                    <IconButton size="small" onClick={() => setColumnSettingsOpen(false)}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {language === 'ar'
                            ? 'حدد الأعمدة المراد عرضها. اسحب لإعادة الترتيب.'
                            : 'Select which columns to display. Drag to reorder.'}
                    </Typography>

                    {/* System Columns */}
                    <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                        {language === 'ar' ? 'حقول النظام' : 'System Fields'}
                    </Typography>
                    <List disablePadding>
                        {columns.filter(col => col.type !== 'form_field').map((column, index) => (
                            <ListItem
                                key={column.id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, columns.indexOf(column))}
                                onDragOver={(e) => handleDragOver(e, columns.indexOf(column))}
                                onDragEnd={handleDragEnd}
                                sx={{
                                    border: '1px solid',
                                    borderColor: draggedItem === columns.indexOf(column) ? 'primary.main' : 'divider',
                                    borderRadius: 1,
                                    mb: 1,
                                    bgcolor: draggedItem === columns.indexOf(column) ? 'action.selected' : 'background.paper',
                                    cursor: 'grab',
                                    '&:active': { cursor: 'grabbing' },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <DragIndicator color="action" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={column.label.startsWith('submissions.') ? t(column.label) : (language === 'ar' ? column.labelAr : column.labelEn)}
                                    secondary={column.required ? (language === 'ar' ? 'مطلوب' : 'Required') : null}
                                />
                                <Switch
                                    edge="end"
                                    checked={column.visible}
                                    onChange={() => handleColumnToggle(column.id)}
                                    disabled={column.required}
                                />
                            </ListItem>
                        ))}
                    </List>

                    {/* Form Field Columns */}
                    {columns.some(col => col.type === 'form_field') && (
                        <>
                            <Typography variant="subtitle2" sx={{ mb: 1, mt: 3 }}>
                                {language === 'ar' ? 'حقول النماذج' : 'Form Fields'}
                            </Typography>
                            <List disablePadding>
                                {columns.filter(col => col.type === 'form_field').map((column) => (
                                    <ListItem
                                        key={column.id}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, columns.indexOf(column))}
                                        onDragOver={(e) => handleDragOver(e, columns.indexOf(column))}
                                        onDragEnd={handleDragEnd}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: draggedItem === columns.indexOf(column) ? 'primary.main' : 'divider',
                                            borderRadius: 1,
                                            mb: 1,
                                            bgcolor: draggedItem === columns.indexOf(column) ? 'action.selected' : 'background.paper',
                                            cursor: 'grab',
                                            '&:active': { cursor: 'grabbing' },
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <DragIndicator color="action" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={language === 'ar' ? column.labelAr : column.labelEn}
                                            secondary={`${column.fieldType}`}
                                        />
                                        <Switch
                                            edge="end"
                                            checked={column.visible}
                                            onChange={() => handleColumnToggle(column.id)}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        size="small"
                        onClick={() => {
                            saveColumnSettings(DEFAULT_COLUMNS)
                        }}
                    >
                        {language === 'ar' ? 'استعادة الافتراضي' : 'Reset to Default'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setColumnSettingsOpen(false)}
                    >
                        {language === 'ar' ? 'تم' : 'Done'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Response Details Dialog */}
            <Dialog
                open={responseDialogOpen}
                onClose={handleCloseResponseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email color="primary" />
                    {language === 'ar' ? 'تفاصيل الرد' : 'Response Details'}
                </DialogTitle>
                <DialogContent dividers>
                    {loadingResponse ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : responseData ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {language === 'ar' ? 'إلى' : 'To'}
                                    </Typography>
                                    <Typography>{responseData.recipient_email}</Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {language === 'ar' ? 'الراد' : 'Responded By'}
                                    </Typography>
                                    <Typography fontWeight={600}>
                                        {language === 'ar' ? responseData.user_name_ar : responseData.user_name_en}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {language === 'ar' ? 'التاريخ' : 'Date'}
                                    </Typography>
                                    <Typography>{formatDate(responseData.sent_at, 'PPP p', language)}</Typography>
                                </Box>
                            </Box>

                            <Divider />

                            {/* Subject - Both Languages */}
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Subject / الموضوع
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                        <Typography variant="caption" color="primary.main" fontWeight={600}>
                                            English
                                        </Typography>
                                        <Typography variant="h6">
                                            {responseData.subject_en || responseData.subject || '-'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 200, textAlign: 'right', direction: 'rtl' }}>
                                        <Typography variant="caption" color="primary.main" fontWeight={600}>
                                            العربية
                                        </Typography>
                                        <Typography variant="h6">
                                            {responseData.subject_ar || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Message - Both Languages */}
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Message / الرسالة
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            flex: 1,
                                            minWidth: 200,
                                            p: 2,
                                            bgcolor: 'background.default',
                                            whiteSpace: 'pre-wrap'
                                        }}
                                    >
                                        <Typography variant="caption" color="primary.main" fontWeight={600} display="block" mb={1}>
                                            English
                                        </Typography>
                                        <Typography>
                                            {responseData.message_en || responseData.message || '-'}
                                        </Typography>
                                    </Paper>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            flex: 1,
                                            minWidth: 200,
                                            p: 2,
                                            bgcolor: 'background.default',
                                            whiteSpace: 'pre-wrap',
                                            textAlign: 'right',
                                            direction: 'rtl'
                                        }}
                                    >
                                        <Typography variant="caption" color="primary.main" fontWeight={600} display="block" mb={1}>
                                            العربية
                                        </Typography>
                                        <Typography>
                                            {responseData.message_ar || '-'}
                                        </Typography>
                                    </Paper>
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center', p: 4 }}>
                            <Typography color="text.secondary">
                                {language === 'ar' ? 'لا توجد تفاصيل للرد' : 'No response details available'}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
                    <Button onClick={handleCloseResponseDialog}>
                        {language === 'ar' ? 'إغلاق' : 'Close'}
                    </Button>
                    {selectedSubmissionForResponse && (
                        <Button
                            variant="contained"
                            startIcon={<Visibility />}
                            onClick={() => {
                                handleCloseResponseDialog()
                                navigate(`/submissions/${selectedSubmissionForResponse.id}`)
                            }}
                        >
                            {language === 'ar' ? 'عرض' : 'View'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Export Language Selection Dialog */}
            <Dialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                maxWidth={false}
                PaperProps={{ sx: { maxWidth: 630, width: '100%' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Download color="primary" />
                    {language === 'ar' ? 'اختر لغة التصدير' : 'Choose Export Language'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {language === 'ar'
                            ? 'اختر اللغة التي تريد استخدامها لأسماء الأعمدة في ملف Excel'
                            : 'Choose the language for column headers in the Excel file'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: '2px solid',
                                borderColor: exportLanguage === 'en' ? 'primary.main' : 'divider',
                                bgcolor: exportLanguage === 'en' ? 'action.selected' : 'background.paper',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={() => setExportLanguage('en')}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: exportLanguage === 'en' ? 'primary.main' : 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {exportLanguage === 'en' && (
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main'
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box>
                                    <Typography fontWeight={600}>English</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Column headers in English
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                cursor: 'pointer',
                                border: '2px solid',
                                borderColor: exportLanguage === 'ar' ? 'primary.main' : 'divider',
                                bgcolor: exportLanguage === 'ar' ? 'action.selected' : 'background.paper',
                                '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={() => setExportLanguage('ar')}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: exportLanguage === 'ar' ? 'primary.main' : 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {exportLanguage === 'ar' && (
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main'
                                            }}
                                        />
                                    )}
                                </Box>
                                <Box>
                                    <Typography fontWeight={600}>العربية</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        أسماء الأعمدة بالعربية
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>

                    {/* System Columns Selection */}
                    <Box sx={{ mt: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Settings sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                    {language === 'ar' ? 'أعمدة النظام' : 'System Columns'}
                                </Typography>
                                <Chip
                                    label={(() => {
                                        const total = SYSTEM_COLUMNS.length;
                                        const selected = selectedSystemColumns === null ? total : selectedSystemColumns.length;
                                        return `${selected}/${total}`;
                                    })()}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 10, fontWeight: 600, color: 'text.secondary', borderColor: 'grey.300' }}
                                />
                            </Box>
                            <Button
                                size="small"
                                onClick={() => {
                                    if (selectedSystemColumns === null || selectedSystemColumns.length === SYSTEM_COLUMNS.length) {
                                        setSelectedSystemColumns([])
                                    } else {
                                        setSelectedSystemColumns(null)
                                    }
                                }}
                                sx={{ textTransform: 'none', fontSize: 11, fontWeight: 500, color: 'text.secondary' }}
                            >
                                {(!selectedSystemColumns || selectedSystemColumns.length === SYSTEM_COLUMNS.length)
                                    ? (language === 'ar' ? 'إلغاء تحديد الكل' : 'Deselect All')
                                    : (language === 'ar' ? 'تحديد الكل' : 'Select All')}
                            </Button>
                        </Box>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1,
                                borderRadius: 2,
                                bgcolor: 'grey.50',
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: 0.5,
                            }}
                        >
                            {SYSTEM_COLUMNS.map((col) => {
                                const label = exportLanguage === 'ar' ? col.labelAr : col.labelEn
                                const isChecked = selectedSystemColumns === null || selectedSystemColumns.includes(col.id)
                                return (
                                    <Paper
                                        key={col.id}
                                        elevation={0}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            border: '1px solid',
                                            borderColor: isChecked ? 'grey.400' : 'grey.200',
                                            bgcolor: isChecked ? 'grey.100' : 'background.paper',
                                            transition: 'all 0.15s ease',
                                            '&:hover': { borderColor: 'grey.500', bgcolor: 'grey.100' },
                                        }}
                                        onClick={() => {
                                            const allIds = SYSTEM_COLUMNS.map(c => c.id)
                                            let current = selectedSystemColumns === null ? [...allIds] : [...selectedSystemColumns]
                                            if (isChecked) {
                                                current = current.filter(id => id !== col.id)
                                            } else {
                                                if (!current.includes(col.id)) current.push(col.id)
                                            }
                                            setSelectedSystemColumns(current.length === allIds.length ? null : current)
                                        }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={isChecked}
                                            sx={{ p: 0.25, color: 'grey.400', '&.Mui-checked': { color: 'grey.600' } }}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: isChecked ? 500 : 400,
                                                color: isChecked ? 'text.primary' : 'text.secondary',
                                                ml: 0.25,
                                                whiteSpace: 'nowrap',
                                                direction: exportLanguage === 'ar' ? 'rtl' : 'ltr',
                                            }}
                                        >
                                            {label}
                                        </Typography>
                                    </Paper>
                                )
                            })}
                        </Paper>
                    </Box>

                    {/* Form Field Selection */}
                    {formFields.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Label sx={{ fontSize: 16, color: 'primary.main' }} />
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {language === 'ar' ? 'حقول النموذج' : 'Form Fields'}
                                    </Typography>
                                    <Chip
                                        label={(() => {
                                            const total = formFields.filter(f => f.type !== 'note').length;
                                            const selected = selectedExportFields === null ? total : selectedExportFields.length;
                                            return `${selected}/${total}`;
                                        })()}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                                    />
                                </Box>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        if (selectedExportFields === null || selectedExportFields.length === formFields.filter(f => f.type !== 'note').length) {
                                            setSelectedExportFields([])
                                        } else {
                                            setSelectedExportFields(null)
                                        }
                                    }}
                                    sx={{ textTransform: 'none', fontSize: 12, fontWeight: 500 }}
                                >
                                    {(!selectedExportFields || selectedExportFields.length === formFields.filter(f => f.type !== 'note').length)
                                        ? (language === 'ar' ? 'إلغاء تحديد الكل' : 'Deselect All')
                                        : (language === 'ar' ? 'تحديد الكل' : 'Select All')}
                                </Button>
                            </Box>
                            <Paper
                                variant="outlined"
                                sx={{
                                    maxHeight: 240,
                                    overflow: 'auto',
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: 'grey.50',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: 0.5,
                                    '&::-webkit-scrollbar': { width: 6 },
                                    '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
                                }}
                            >
                                {formFields.filter(f => f.type !== 'note').map((field) => {
                                    const fieldId = field.id || field.name
                                    const label = exportLanguage === 'ar'
                                        ? (field.label_ar || field.label_en || field.label || fieldId)
                                        : (field.label_en || field.label || fieldId)
                                    const isChecked = selectedExportFields === null || selectedExportFields.includes(fieldId)
                                    return (
                                        <Paper
                                            key={fieldId}
                                            elevation={0}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 1.5,
                                                cursor: 'pointer',
                                                border: '1px solid',
                                                borderColor: isChecked ? 'primary.light' : 'grey.200',
                                                bgcolor: isChecked ? 'primary.50' : 'background.paper',
                                                transition: 'all 0.15s ease',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    bgcolor: isChecked ? 'primary.50' : 'action.hover',
                                                },
                                            }}
                                            onClick={() => {
                                                const allIds = formFields.filter(f => f.type !== 'note').map(f => f.id || f.name)
                                                let current = selectedExportFields === null ? [...allIds] : [...selectedExportFields]
                                                if (isChecked) {
                                                    current = current.filter(id => id !== fieldId)
                                                } else {
                                                    if (!current.includes(fieldId)) current.push(fieldId)
                                                }
                                                setSelectedExportFields(current.length === allIds.length ? null : current)
                                            }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={isChecked}
                                                sx={{
                                                    p: 0.5,
                                                    color: 'grey.400',
                                                    '&.Mui-checked': { color: 'primary.main' },
                                                }}
                                                tabIndex={-1}
                                                disableRipple
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: 13,
                                                    fontWeight: isChecked ? 500 : 400,
                                                    color: isChecked ? 'text.primary' : 'text.secondary',
                                                    ml: 0.5,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    direction: exportLanguage === 'ar' ? 'rtl' : 'ltr',
                                                }}
                                            >
                                                {label}
                                            </Typography>
                                        </Paper>
                                    )
                                })}
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportDialogOpen(false)}>
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={handleConfirmExport}
                    >
                        {language === 'ar' ? 'تصدير' : 'Export'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default Submissions
